import { Vec4, Vec3, Matrix4 } from 'ayvri-std/lib/vector';
import { cartographicToCartesian, eastNorthUpToWorld } from 'ayvri-std/lib/ellipsoid';
import { offsetPosition, defaultValue, defined } from './helpers';
import PlayerState from './PlayerState';
import * as AvrMath from './Math';
import { setCameraDistance } from './Actions';

const kMinTargetDist = 50;
const kMaxTargetDist = 1500000;
const kMaxPitch = (80 / 180) * Math.PI;
const kMinPitch = -(80 / 180) * Math.PI;
const kMinCameraWeight = 0.0001;

const scratchView = new Matrix4();
const scratchCam = new Matrix4();
const scratchForward = new Vec4();
const scratchRight = new Vec4();
const scratchUp = new Vec4();
const scratchPos = new Vec4();

/******************************************************************************
 * CameraSystem manages a stack of Cameras. Each frame it resolves a final
 *   Camera based on interpolations of the stack based on weight.
 *****************************************************************************/
export default class CameraSystem {
  constructor() {
    this.followCam = new FollowCam();
    this.heightSampler = null;
    this.controlWeight = 1.0;
    this.stack = [];

    this.camera = new Camera();
    this.pos = null;

    this.input = null;
    this.lastCameraDistance = null;

    // Properties derived from Camera
    this.worldToTarget = new Matrix4();
    this.targetToWorld = new Matrix4();

    this.worldToCam = new Matrix4();
    this.camToWorld = new Matrix4();

    this.pos = new Vec3();
    this.forward = new Vec3();
    this.right = new Vec3();
    this.up = new Vec3();
  }

  onSceneLoad(scene, dispatch, input, options, heightSampler) {
    this.heightSampler = heightSampler;
    this.input = input;

    dispatch({ type: 'SET_CAMERA_TARGET', target: scene.startPoint });
    dispatch(setCameraDistance(scene.sceneSettings.targetDistance));

    // Add our default base camera
    this.addCamera(this.followCam, {
      targetPitch: scene.sceneSettings.targetPitch,
    });

    if (options.enableCaptureCam) {
      this.addCamera(new CaptureCam(), {
        target: cartographicToCartesian(scene.startPoint.latitude, scene.startPoint.longitude, scene.startPoint.height),
        offset: 10000,
      });
    }
  }

  addCamera(camera, settings = {}) {
    camera.initialize(settings, this.heightSampler);
    this.stack.push(camera);
  }

  update(store, clock, eventEmitter) {
    if (!this.heightSampler) {
      return;
    }
    const state = store.getState();

    // Manage our base FollowCam
    const playhead = state.playheads[state.tracks.activeTrack];
    if (playhead && playhead.ready) {
      store.dispatch({ type: 'SET_CAMERA_TARGET', target: playhead.cartesian });
    }
    this.followCam.setTargetDistance(state.cameraTarget.targetDistance);
    this.followCam.setTarget(state.cameraTarget.target);

    // Construct an object with useful information for the camera update
    const camInfo = {
      state: store.getState(),
      clock: clock,
      input: this.input,
      controlWeight: this.camera.controlWeight,
    };

    // Update individual cameras
    this.camera.clear();
    const activeCams = [];
    for (let i = 0; i < this.stack.length; i++) {
      const cam = this.stack[i];
      activeCams[i] = cam.update(camInfo, this.camera);
    }

    // Remove any cameras that no longer contribute
    this.stack = this.stack.filter((c, i) => activeCams[i]);

    // @HACK:
    if (state.cameraTarget.targetDistance !== this.followCam.targetDist) {
      store.dispatch(setCameraDistance(this.followCam.targetDist));
    }

    // Notify any listeners if the camera distance has changed
    const currentDistance = store.getState().cameraTarget.targetDistance;
    if (currentDistance !== this.lastCameraDistance) {
      eventEmitter.emit('camera.distanceChanged', currentDistance);
      this.lastCameraDistance = currentDistance;
    }

    // Prevent Camera NaN's from leaking out
    // @NOTE: Globe does not throw any errors with a bad camera, it will just silently fail to render
    if (!this.camera.isValid()) throw new Error(`Invalid camera: ${JSON.stringify(this.camera, null, 1)}`);

    // Compute some useful camera values
    this._computeProperties(this.camera);

    // @TODO: Delay before returning to ideal pitch after button release
    // @TODO: Snap back to a maximum zoom level once the user releases the mouse, maybe?
    // @TODO: Investigate pausing tile requests during FocusCam events
  }

  _computeProperties(camera) {
    const pos = camera.offset.toCartesian(this.pos).negate();
    const dir = Vec3.negative(pos, scratchForward).normalize();

    // @TODO: Support pitch, roll, yaw
    const right = Vec3.cross(dir, Vec3.UNIT_Z, scratchRight).normalize();
    const up = Vec3.cross(right, dir, scratchUp).normalize();
    const targetToCam = Matrix4.computeView(pos, dir, up, right, scratchView);
    const camToTarget = Matrix4.fromColumns(right, up, dir, Vec4.fromVec3(pos, 1.0, scratchPos), scratchCam);

    eastNorthUpToWorld(camera.target, this.targetToWorld, this.worldToTarget);
    this.worldToCam = Matrix4.multiply(targetToCam, this.worldToTarget, this.worldToCam);
    this.camToWorld = Matrix4.multiply(this.targetToWorld, camToTarget, this.camToWorld);

    this.pos = this.camToWorld.getColumnXYZ(3, this.pos);
    this.forward = this.camToWorld.getColumnXYZ(2, this.forward);
    this.right = this.camToWorld.getColumnXYZ(0, this.right);
    this.up = this.camToWorld.getColumnXYZ(1, this.up);
  }
}

/******************************************************************************
 * Cameras
 *****************************************************************************/
export class Camera {
  constructor() {
    this.target = new Vec3();
    this.offset = new SphereCoord();
    this.pitch = 0.0;
    this.roll = 0.0;
    this.yaw = 0.0;
    this.fov = toRadians(60.0);
    this.controlWeight = 1.0;
  }

  clear() {
    this.target.init();
    this.offset.init();
    this.pitch = 0.0;
    this.roll = 0.0;
    this.yaw = 0.0;
    this.fov = 0.0;
    this.controlWeight = 1.0;
  }

  isValid() {
    return (
      Number.isFinite(this.target.x) &&
      Number.isFinite(this.target.y) &&
      Number.isFinite(this.target.z) &&
      Number.isFinite(this.offset.distance) &&
      (this.offset.polar >= -Math.PI && this.offset.polar <= Math.PI) &&
      (this.offset.azimuthAngle >= -Math.PI && this.offset.azimuthAngle <= Math.PI) &&
      (this.pitch >= -Math.PI && this.pitch <= Math.PI) &&
      (this.roll >= -2 * Math.PI && this.roll <= 2 * Math.PI) &&
      (this.yaw >= -2 * Math.PI && this.yaw <= 2 * Math.PI) &&
      (this.fov > 0.0 && this.fov < 2 * Math.PI)
    );
  }

  copy(dest) {
    dest.target.copy(this.target);
    dest.offset.copy(this.offset);
    dest.pitch = this.pitch;
    dest.roll = this.roll;
    dest.yaw = this.yaw;
    dest.fov = this.fov;
  }
}

export class FollowCam extends Camera {
  initialize(settings = {}, heightSampler) {
    // @TODO: We shouldn't need these
    this.heightSampler = heightSampler;

    this.targetToWorld = new Matrix4();
    this.worldToTarget = new Matrix4();

    this.reset(settings);
  }

  reset(settings) {
    // Settings
    // [0.0-0.5] The rough percentage of the screen in which the sky is visible
    this.skyAmount = defaultValue(settings.skyRatio, 0.2);

    // If a targetPitch is not specified, skyAmount is used to dynamically compute pitch based on Y-FOV
    this.targetPitch = defaultValue(settings.targetPitch, undefined);

    // Rotation speed of camera while paused (in radians/second)
    this.spinRate = defaultValue(settings.spinRate, toRadians(15));

    // Number of samples to take along the view ray while attempting to keep the camera target unoccluded
    this.lineOfSightSteps = defaultValue(settings.lineOfSightSteps, 180);

    // Camera distance from target, in meters
    this.targetDist = defaultValue(settings.targetDistance, 1000);

    // Transients
    this.first = true;
    this.goalWeight = 1.0;
    this.targetVel = new Vec3();
    this.offsetVel = new Vec3();
    this.camPos = new Vec3();
    this.camVel = new Vec3();
    this.targetGoal = new Vec3();
    this.targetDistDelta = 0.0;
    this.fadeOutSpring = new Spring(defaultValue(settings.fadeOutTime, 1.0), 1.0);

    // Default starting orientation
    this.offset.azimuth = { x: -1.0, y: -1.0 };
    this.offset.polar = -Math.PI * 0.1;
    this.offset.distance = 1000;
  }

  setTarget(target) {
    this.targetGoal = pointToVec3(target, this.targetGoal);
  }

  setTargetDistance(distance) {
    this.targetDist = distance;
  }

  stop() {
    this.goalWeight = 0.0;
  }

  update({ state, clock, input, controlWeight }, resultCam) {
    const dt = clock.realDt / 1000;

    if (this.first) {
      if (resultCam.isValid()) {
        // If there's an existing camera, start at it's position and orientation
        this.target.copy(resultCam.target);
        this.offset.copy(resultCam.offset);
      } else {
        // Otherwise start at our goal position and orientation
        this.target.copy(this.targetGoal);
      }

      // Set the buffered camera position so that we interpolate correctly
      eastNorthUpToWorld(this.target, this.targetToWorld, this.worldToTarget);
      this.offset.toCartesian(this.camPos);
      this.camPos = this.targetToWorld.transformPoint3(this.camPos.negate());
      this.first = false;
    }

    // Move the current target towards the goal with spring and drag forces
    const oldTarget = this.target.clone();
    dampedSpring(this.target, this.targetVel, this.targetGoal, 2.0, dt);

    const deltaTarget = Vec3.subtract(this.target, oldTarget, new Vec3());
    this.targetDistDelta = deltaTarget.length() - this.targetDist;

    // Attempt to move the camera as little as possible while constraining
    //   target distance and pitch in target space.
    eastNorthUpToWorld(this.target, this.targetToWorld, this.worldToTarget);
    const camPosTarget = this.worldToTarget.transformPoint3(this.camPos);
    let camGoalTarget = camPosTarget.clone();
    {
      // Spin the camera slowly for when target is paused
      const paused = state.loading.playerState >= PlayerState.PLAYING && !state.clock.playing;
      if (paused && !input.isRotating) {
        const spinRate = this.spinRate * controlWeight; // x degrees per second
        const spinMtx = Matrix4.fromRotationZ(spinRate * dt);
        camGoalTarget = spinMtx.transformPoint3(camGoalTarget);
      }

      // Return pitch to ideal when the user is not interacting
      if (!input.isRotating) {
        // Compute the ideal pitch near the ground based on the amount of sky we want to see
        const fovPercent = Math.max(0.0, 0.5 - this.skyAmount);
        const groundPitch = getVerticalFov(this.fov, state) * fovPercent;

        // If the user has requested to remain at a specific pitch, oblige.
        const userPitch = defaultValue(this.targetPitch, groundPitch);

        // Ideal pitch at altitude is straight down
        const spacePitch = Math.PI * 0.5;

        // Interpolate between the pitches based on target distance
        const kGroundPitchDistance = 10000;
        const kSpacePitchDistance = 1500000;
        const t = AvrMath.delerp(kGroundPitchDistance, kSpacePitchDistance, this.targetDist);
        const pitch = AvrMath.lerp(userPitch, spacePitch, AvrMath.saturate(t));

        const lengthXY = Math.sqrt(camGoalTarget.x * camGoalTarget.x + camGoalTarget.y * camGoalTarget.y);
        camGoalTarget.z = Math.tan(pitch) * lengthXY;
      }

      // Enforce length
      Vec3.multiply(camGoalTarget, this.targetDist / camGoalTarget.length(), camGoalTarget);

      // Try to maintain line of sight
      const camGoalWorld = this.targetToWorld.transformPoint3(camGoalTarget);
      const heightOffset = camGoalTarget.z * 0.15;
      const modified = this.heightSampler.applyLineOfSight(
        this.target,
        camGoalWorld,
        heightOffset,
        this.lineOfSightSteps,
      );
      if (modified) {
        camGoalTarget = this.worldToTarget.transformPoint3(camGoalWorld);
      }

      // Re-enforce length otherwise camera goes very high on cliff faces
      Vec3.multiply(camGoalTarget, this.targetDist / camGoalTarget.length(), camGoalTarget);
    }

    // Convert back to world space
    const camGoal = this.targetToWorld.transformPoint3(camGoalTarget);

    // Rotate based on input
    if (input.rotateMotion) {
      const rotateRate = 0.88 * controlWeight;

      const deltaAzi = rotateRate * input.rotateMotion.x * 2.0 * Math.PI;
      let deltaPolar = rotateRate * input.rotateMotion.y * Math.PI;

      // Disable inertia for pitch control
      if (!input.isRotating) {
        deltaPolar = 0.0;
      }

      const pitchVec = new Vec3(-camGoalTarget.y, camGoalTarget.x, 0).normalize();
      const polarMtx = Matrix4.fromRotationXY(deltaPolar, pitchVec);
      const spinMtx = Matrix4.fromRotationZ(deltaAzi);

      let newGoalTarget = polarMtx.transformPoint3(camGoalTarget);
      newGoalTarget = spinMtx.transformPoint3(newGoalTarget);

      const newGoal = this.targetToWorld.transformPoint3(newGoalTarget);
      const rotVec = Vec3.subtract(newGoal, camGoal, new Vec3());
      Vec3.add(this.camVel, rotVec, this.camVel);
    }

    // Zoom based on input
    if (input.zoomMotion) {
      const kMinZoomRate = 100.0;
      const kMaxZoomRate = 1000000.0;

      // Zoom rate in meters per pixel. Less when closer to target, huge when in space
      const zoomFactor = 4.0;
      let zoomRate = this.targetDist - kMinTargetDist;
      zoomRate *= zoomFactor * controlWeight;
      zoomRate = AvrMath.clamp(zoomRate, kMinZoomRate, kMaxZoomRate);

      let zoomDist = zoomRate * -input.zoomMotion;
      const newDist = AvrMath.clamp(this.targetDist + zoomDist, kMinTargetDist, kMaxTargetDist);
      zoomDist = newDist - this.targetDist;
      this.targetDist = newDist;

      // Add some velocity to that zooming feels responsive
      const zoomVec = this.targetToWorld.transformVec3(camPosTarget).normalize();
      Vec3.multiply(zoomVec, zoomDist, zoomVec);
      Vec3.add(this.camVel, zoomVec, this.camVel);
    }

    // Move the camera towards the goal in world space
    dampedSpring(this.camPos, this.camVel, camGoal, 1.0, dt);

    // Avoid ground penetration
    this.heightSampler.enforceAboveGround(this.camPos, 10.0);

    // Store the camera offset from the target in target space spherical coords
    const camPosT = this.worldToTarget.transformPoint3(this.camPos).negate();
    this.offset.fromCartesian(camPosT);

    // Constrain the pitch
    const clampedPitch = AvrMath.clamp(this.offset.polar, kMinPitch, kMaxPitch);
    if (clampedPitch !== this.offset.polar) {
      this.offset.polar = clampedPitch;
      this.offset.toCartesian(camPosT);
      this.camPos = this.targetToWorld.transformPoint3(camPosT.negate());
    }

    // If the user is requesting to set the pitch, record the goal pitch (not the current) as the new target
    if (input.isModifyingPitch) {
      const camGoalT = this.worldToTarget.transformPoint3(camGoal);
      this.targetPitch = Math.asin(camGoalT.z / camGoalT.length());
    }

    // Fade out if necessary
    const weight = this.fadeOutSpring.integrate(this.goalWeight, dt);

    // Set the camera
    Vec3.lerp(resultCam.target, this.target, weight, resultCam.target);
    Vec4.lerp(resultCam.offset, this.offset, weight, resultCam.offset);
    resultCam.fov = AvrMath.lerp(resultCam.fov, this.fov, weight);

    return weight >= kMinCameraWeight;
  }
}

export class IntroCam extends Camera {
  initialize(settings) {
    this.lifetime = settings.duration;

    const endCarto = settings.startPoint;
    const offsetPoint = offsetPosition(endCarto.latitude, endCarto.longitude, 500);

    const startCarto = {
      latitude: offsetPoint.lat,
      longitude: offsetPoint.lon,
      height: endCarto.height + 15000,
    };

    this.startPoint = cartographicToCartesian(startCarto.latitude, startCarto.longitude, startCarto.height, new Vec3());
    this.endPoint = cartographicToCartesian(endCarto.latitude, endCarto.longitude, endCarto.height, new Vec3());

    this.target = this.endPoint;
    const targetToWorld = new Matrix4();
    const worldToTarget = new Matrix4();
    eastNorthUpToWorld(this.target, targetToWorld, worldToTarget);

    const result = worldToTarget.transformPoint3(this.startPoint);
    this.offset.fromCartesian(result.negate());
  }

  start() {
    this.startTime = Date.now();
  }

  update({ state }, resultCam) {
    let weight = 1.0;

    if (this.startTime) {
      const time = Date.now() - this.startTime;
      weight = AvrMath.smoothstep(1.0, 0.0, AvrMath.smoothstep(0, this.lifetime, time));
    } else if (state.loading.playerState >= PlayerState.PLAYING) {
      this.start();
    }

    // Update the result camera
    Vec3.lerp(resultCam.target, this.target, weight, resultCam.target);
    Vec4.lerp(resultCam.offset, this.offset, weight, resultCam.offset);

    // Disable user controls
    resultCam.controlWeight = AvrMath.lerp(resultCam.controlWeight, 0.0, weight);

    return weight >= kMinCameraWeight;
  }
}

export class FocusCam extends Camera {
  initialize(options) {
    this.fadeTime = defaultValue(options.fadeTime, 1.0);

    this.lifetime = options.duration || Number.POSITIVE_INFINITY;
    this.startTime = Date.now();

    this.goalWeight = 1.0;
    this.weightSpring = new Spring(this.fadeTime, kMinCameraWeight, 0.0);

    this.targetSpring = new Spring(this.fadeTime, 1.0, 0.0);
    this.setTarget(options.target, defaultValue(options.distance, 200));
    this.targetSpring.setValue(1);
  }

  stop() {
    this.goalWeight = 0.0;
  }

  setTarget(target, distance) {
    this.lastTarget = this.target;
    this.lastDistance = this.offset.distance;

    this.goalTarget = target;
    this.goalDistance = distance;

    // If we're setting the target for the first time, snap to it instead of lerping
    const wasSet = this.lastTarget.length() > 0.0;
    this.targetSpring.setValue(wasSet ? 0 : 1);
  }

  update({ clock }, resultCam) {
    const time = Date.now() - this.startTime;
    const dt = clock.realDt / 1000.0;

    if (time > this.lifetime) {
      this.goalWeight = 0.0;
    }

    // Use a critically dampened spring to tween the weight so we blend smoothly
    const weight = this.weightSpring.integrate(this.goalWeight, dt);
    const targetWeight = this.targetSpring.integrate(1.0, dt);

    this.target = Vec3.lerp(this.lastTarget, this.goalTarget, targetWeight, this.target);
    this.offset.distance = AvrMath.lerp(this.lastDistance, this.goalDistance, targetWeight);

    // Update the result camera
    Vec3.lerp(resultCam.target, this.target, weight, resultCam.target);
    resultCam.offset.distance = AvrMath.lerp(resultCam.offset.distance, this.offset.distance, weight);

    // Disable user controls
    resultCam.controlWeight = AvrMath.lerp(resultCam.controlWeight, 0.0, weight);

    return weight >= kMinCameraWeight;
  }
}

export class CaptureCam extends Camera {
  initialize(options, heightSampler) {
    this.target = options.target.clone();
    this.distance = options.offset || 200;
    this.heightSampler = heightSampler;

    this.targetToWorld = new Matrix4();
    this.worldToTarget = new Matrix4();
    eastNorthUpToWorld(this.target, this.targetToWorld, this.worldToTarget);
  }

  update({ state }, resultCam) {
    // Compute the ideal pitch near the ground based on the amount of sky we want to see
    const skyAmount = 0.2;
    const fovPercent = Math.max(0.0, 0.5 - skyAmount);
    const pitch = getVerticalFov(this.fov, state) * fovPercent;
    this.offset.polar = -pitch;

    // Line of Sight
    const heightOffset = 200; // @NOTE: This number affects quality a lot
    const lineOfSightSteps = 200;
    const offset = this.offset.toCartesian(new Vec3());
    offset.negate();
    const camWorld = this.targetToWorld.transformPoint3(offset);
    const modified = this.heightSampler.applyLineOfSight(this.target, camWorld, heightOffset, lineOfSightSteps);
    if (modified) {
      const camTarget = this.worldToTarget.transformPoint3(camWorld);
      camTarget.negate();
      this.offset.fromCartesian(camTarget);
    }

    // Distance must be constrained after line of sight as it can be modified
    this.offset.distance = this.distance;

    // Update the result camera
    resultCam.target = this.target.clone();
    resultCam.offset.polar = this.offset.polar;
    resultCam.offset.azimuth = this.offset.azimuth;
    resultCam.offset.distance = this.offset.distance;

    // Disable user controls
    resultCam.controlWeight = 0.0;

    return true;
  }
}

export class FixedCam extends Camera {
  initialize(options) {
    this.target.copy(options.target);
    this.offset.copy(options.offset);

    if (defined(options.pitch)) this.pitch = options.pitch;
    if (defined(options.roll)) this.roll = options.roll;
    if (defined(options.yaw)) this.yaw = options.yaw;
    if (defined(options.fov)) this.fov = options.fov;

    this.active = true;
  }

  update(options, resultCam) {
    this.copy(resultCam);
    return this.active;
  }
}

/******************************************************************************
 * Spherical Coordinates
 *  Azimuth starts at North and tends East
 *  Polar elevation is from the XY/EastNorth plane, tending up
 *  Distance is the range in meters
 *****************************************************************************/
class SphereCoord extends Vec4 {
  get distance() {
    return this.w;
  }
  get azimuth() {
    return { x: this.x, y: this.y };
  }
  get polar() {
    return this.z;
  }

  // @NOTE: Azimuth is stored as a 2D angle so that it interpolates correctly.
  //   Since the angular representation wraps, it is easy for it to take the
  //   long way around. E.g 355->5 will cause a rotatation of 350 degrees rather
  //   than 10. Once interpolation is complete we can convert back to the angle.
  // See https://www.gdcvault.com/play/1014606/Iterating-on-a-Dynamic-Camera
  get azimuthAngle() {
    return Math.atan2(this.x, this.y);
  }

  set distance(a) {
    this.w = a;
  }
  set azimuth(v) {
    this.x = v.x;
    this.y = v.y;
  }
  set polar(a) {
    this.z = a;
  }

  fromCartesian(p) {
    this.distance = p.length();
    this.polar = Math.asin(p.z / this.distance);
    this.azimuth = {
      x: p.x / this.distance,
      y: p.y / this.distance,
    };
  }

  toCartesian(r) {
    const theta = this.azimuthAngle;
    r.x = this.distance * Math.cos(this.polar) * Math.sin(theta);
    r.y = this.distance * Math.cos(this.polar) * Math.cos(theta);
    r.z = this.distance * Math.sin(this.polar);
    return r;
  }
}

/******************************************************************************
 * Helpers
 *****************************************************************************/
function pointToVec3(point, result) {
  if (point.x !== undefined) {
    return result.init(point.x, point.y, point.z);
  } else {
    return cartographicToCartesian(point.latitude, point.longitude, point.height, result);
  }
}

function dampedSpring(pos, vel, goal, time90, dt) {
  const c0 = (dt * 3.75) / time90;
  if (c0 >= 1.0) {
    // Spring is too stiff, go to goal to prevent oscillation
    pos.copy(goal);
    vel.init(0.0, 0.0, 0.0);
    return;
  }

  const delta = Vec3.subtract(goal, pos, new Vec3());
  const force = Vec3.subtract(delta, Vec3.multiply(vel, 2.0, new Vec3()), new Vec3());

  Vec3.add(pos, Vec3.multiply(vel, c0, new Vec3()), pos);
  Vec3.add(vel, Vec3.multiply(force, c0, new Vec3()), vel);
}

class Spring {
  constructor(time90 = 1.0, initialVal = undefined, initialVel = 0.0) {
    this.val = initialVal;
    this.vel = initialVel;
    this.time90 = time90;
  }

  setValue(val) {
    this.val = val;
  }

  integrate(goal, dt) {
    if (this.initialVal === undefined) {
      this.initialVal = goal;
    }

    const c0 = (dt * 3.75) / this.time90;
    if (c0 >= 1.0) {
      // Spring is too stiff, go to goal to prevent oscillation
      this.val = goal;
      this.vel = 0.0;
      return this.val;
    }

    const delta = goal - this.val;
    const force = delta - 2.0 * this.vel;

    this.val += this.vel * c0;
    this.vel += force * c0;

    return this.val;
  }
}

function toRadians(d) {
  return (d * Math.PI) / 180.0;
}

function getVerticalFov(fov, state) {
  // Vertical FOV is the FOV if height is greater than width
  const aspectRatio = state.layout.height / state.layout.width;
  return Math.min(fov, fov * aspectRatio);
}
